import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

const ClientSidebarContext = createContext(null);

const MOBILE_BREAKPOINT = 992;
const STORAGE_KEY = "rayabot-client-sidebar-open";

const getIsMobile = () =>
  typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT;

const readStoredOpenState = () => {
  if (typeof window === "undefined") return true;
  const saved = localStorage.getItem(STORAGE_KEY);
  return saved === null ? true : saved === "true";
};

const ClientSidebarProvider = ({ children }) => {
  const [isMobile, setIsMobile] = useState(getIsMobile);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() =>
    getIsMobile() ? false : readStoredOpenState()
  );

  useEffect(() => {
    const handleResize = () => {
      const mobile = getIsMobile();

      setIsMobile((prevMobile) => {
        if (mobile !== prevMobile) {
          setIsSidebarOpen(mobile ? false : readStoredOpenState());
        }
        return mobile;
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => {
      const next = !prev;
      if (!isMobile) {
        localStorage.setItem(STORAGE_KEY, String(next));
      }
      return next;
    });
  }, [isMobile]);

  const closeSidebar = useCallback(() => setIsSidebarOpen(false), []);

  const sidebarWidth = isMobile ? 0 : isSidebarOpen ? 260 : 72;

  useEffect(() => {
    document.documentElement.style.setProperty("--sidebar-w", `${sidebarWidth}px`);
  }, [sidebarWidth]);

  return (
    <ClientSidebarContext.Provider
      value={{ isSidebarOpen, toggleSidebar, closeSidebar, isMobile, sidebarWidth }}
    >
      {children}
    </ClientSidebarContext.Provider>
  );
};

const useClientSidebar = () => {
  const context = useContext(ClientSidebarContext);
  if (!context) {
    throw new Error("useClientSidebar must be used inside ClientSidebarProvider");
  }
  return context;
};

export { ClientSidebarProvider, useClientSidebar };